import { DataTable } from 'components/table/DataTable';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { CostConfigurationUpload, CostUploadStatus } from 'components/profitability/configQueryHooks';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { DetailsColumnWidget } from 'pages/Profitability/costConfigurations/components/DetailsColumnWidget';
import { Chip } from '@mui/material';
import { getChipSxStylingForLevel } from 'styles/utils';
import { formatCostConfigurationDate } from '../formatCostConfigurationDate';

export interface CostUploadTableProps {
  uploads: CostConfigurationUpload[];
}

const useColumns = (): ColumnDef<CostConfigurationUpload>[] => {
  const intl = useIntl();

  const paintChip = (status: CostUploadStatus) => {
    const chipLevel = status === 'FAILED' ? 'Critical' : 'Success';
    const label = status === 'FAILED' ? 'failed' : 'validated';

    return (
      <Chip
        sx={getChipSxStylingForLevel(chipLevel)}
        label={intl.formatMessage({ id: `profitability.cost-configurations.upload.table.column.status.${label}` })}
      />
    );
  };

  const formatDate = (iso: string) => {
    if (!iso) return '';
    const date = new Date(`${iso.toString()}Z`); // 'Z' points to the fact it's the UTC date
    const dateString = formatCostConfigurationDate(intl, date);
    const timeString = intl.formatDate(date, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${dateString} ${timeString}`;
  };

  const header = (colId: string) =>
    intl.formatMessage({ id: `profitability.cost-configurations.upload.table.column.${colId}` });

  return [
    {
      id: 'fileName',
      accessorKey: 'fileName',
      header: header('fileName'),
      cell: context => context.getValue(),
    },
    {
      id: 'changedFiles',
      accessorKey: 'changedFiles',
      header: header('changedFiles'),
      cell: context => context.getValue(),
    },
    {
      id: 'user',
      accessorKey: 'user',
      header: header('user'),
      cell: context => context.getValue(),
    },
    {
      id: 'dateTime',
      accessorKey: 'uploadDateTime',
      header: header('dateTime'),
      cell: context => formatDate(context.getValue() as string),
    },
    {
      id: 'uploadStatus',
      accessorKey: 'uploadStatus',
      header: header('uploadStatus'),
      cell: context => paintChip(context.getValue() as CostUploadStatus),
    },
    {
      id: 'details',
      header: header('details'),
      cell: context => <DetailsColumnWidget uploadDetails={context.row.original} />,
    },
  ];
};

export function CostUploadTable({ uploads }: CostUploadTableProps) {
  const columns: ColumnDef<CostConfigurationUpload>[] = useColumns();

  return (
    <DataTableContextProvider>
      <DataTable
        titleId={'profitability.cost-configurations.configFiles.title'}
        rows={uploads}
        columns={columns}
        defaultSortingState={[{ id: 'dateTime', desc: true }]}
        testId={`profitability-$cost-upload-table`}
      />
    </DataTableContextProvider>
  );
}
